import { useState } from "react";
import { OfferResponse, OrderState } from "../../types";
import { isBiddingClosed } from "../../utils/biddingTime";
import {
  LinePriceInput,
  LinePriceInputs,
  linePriceInputsOf,
  linePricesTotal,
  offerCurrencyOf,
  pricedLinesOf,
} from "../../utils/linePricing";

/** SCRUM-116: a line-priced offer also drafts its line prices; `price` follows their total. */
type EditingMap = Record<
  string,
  { price: string; currency: string; linePrices?: LinePriceInputs }
>;

/**
 * Per-line normal-price edit state for the joint seller view (Task 6). Extracted from
 * `JointSellerOffer.tsx` so the container stays under the size budget and this cohesive
 * "which lines are being edited, and their draft price/currency" state is testable on its own.
 * The save itself stays in the container (it drives a mutation); this owns only the edit drafts
 * and `updatingOfferId`.
 */
export const useJointSellerEditing = () => {
  const [editingOffers, setEditingOffers] = useState<EditingMap>({});
  const [updatingOfferId, setUpdatingOfferId] = useState<string | null>(null);

  const onStartEdit = (offer: OfferResponse) => {
    if (
      offer?.order?.state !== OrderState.OPEN ||
      isBiddingClosed(offer?.order) ||
      offer?.selected
    )
      return;
    setEditingOffers((prev) => ({
      ...prev,
      [offer.id]: {
        price: String(offer?.price || 0),
        currency: offer?.currency ?? "",
        ...(pricedLinesOf(offer?.order).length > 0
          ? {
              linePrices: linePriceInputsOf(
                pricedLinesOf(offer?.order),
                offer?.linePrices,
                offer?.currency ?? ""
              ),
            }
          : {}),
      },
    }));
  };

  /** Changes one line's price or currency; the offer's price and currency follow its lines. */
  const onEditLinePriceChange = (
    offer: OfferResponse,
    code: string,
    patch: Partial<LinePriceInput>
  ) =>
    setEditingOffers((prev) => {
      const current = prev[offer.id]?.linePrices ?? {};
      const linePrices = { ...current, [code]: { ...current[code], ...patch } };
      const lines = pricedLinesOf(offer.order);
      const total = linePricesTotal(lines, linePrices);
      return {
        ...prev,
        [offer.id]: {
          currency: offerCurrencyOf(lines, linePrices),
          price: total > 0 ? String(total) : "",
          linePrices,
        },
      };
    });

  const onCancelEdit = (offerId: string) =>
    setEditingOffers((prev) => {
      const next = { ...prev };
      delete next[offerId];
      return next;
    });

  const onEditPriceChange = (offerId: string, value: string) =>
    setEditingOffers((prev) => ({
      ...prev,
      [offerId]: {
        ...prev[offerId],
        price: value,
        currency: prev[offerId]?.currency ?? "",
      },
    }));

  const onEditCurrencyChange = (offerId: string, value: string) =>
    setEditingOffers((prev) => ({
      ...prev,
      [offerId]: {
        ...prev[offerId],
        price: prev[offerId]?.price ?? "",
        currency: value,
      },
    }));

  const resetEditing = () => {
    setEditingOffers({});
    setUpdatingOfferId(null);
  };

  return {
    editingOffers,
    updatingOfferId,
    setUpdatingOfferId,
    onStartEdit,
    onCancelEdit,
    onEditPriceChange,
    onEditCurrencyChange,
    onEditLinePriceChange,
    resetEditing,
  };
};
